"use client";

/* ————— Asked inside Sola —————
   The kinds of questions Sola is built to answer, presented generally
   (NOT as verbatim quotes from identifiable users — that reads as
   creepy surveillance; Jay 2026-07-11). They slide in from alternating
   sides; the last one gets answered with a faithful excerpt of a Sola
   reply, framed as an example, not an attributed conversation.

   Motion (Jay direction, 2026-07-10):
   - Everything is scroll-scrubbed via <Scrub>/<ScrubWords> so progress
     tracks the scrollbar in both directions.
   - Each query gets its own generous vertical beat so you read one at
     a time.
   - The answer reveals like the app's chat: per-word left-to-right
     opacity cascade (WordStream), mapped onto scroll.
*/

import Link from "next/link";
import { Scrub, ScrubWords } from "./Scrub";

const QUERIES: { text: string; side: "left" | "right" }[] = [
  { text: "Why does God feel silent when I pray?", side: "left" },
  { text: "What does “covenant” actually mean?", side: "right" },
  { text: "Why is Jesus called the Word in John 1?", side: "left" },
  { text: "What can I pray when I’m anxious?", side: "right" },
  { text: "Can you lose your salvation?", side: "left" },
];

export default function QueriesAnswered() {
  return (
    <section
      data-folio="fol. 2v"
      data-folio-title="Asked and answered"
      className="relative py-28 sm:py-40"
    >
      <div className="container">
        <Scrub from="up">
          <div className="max-w-2xl">
            <p className="rubric mb-4">Questions Sola is built for</p>
            <h2 className="text-balance mb-3">
              The questions you bring.
              <br />
              Answers you can check.
            </h2>
            <p className="text-muted-foreground max-w-lg">
              The kind of thing you open Sola to ask.
            </p>
          </div>
        </Scrub>

        {/* The queries — one per beat: generous space so each gets read
            alone; the scrub window does the pacing. */}
        <div className="mt-24 sm:mt-32 flex flex-col gap-28 sm:gap-40 max-w-3xl mx-auto">
          {QUERIES.map((q) => (
            <Scrub
              key={q.text}
              as="figure"
              from={q.side}
              start="top 88%"
              end="top 52%"
              className={`max-w-[34rem] ${q.side === "right" ? "self-end text-right" : "self-start"}`}
            >
              <blockquote className="font-scripture italic text-xl sm:text-[1.5rem] leading-snug text-foreground">
                &ldquo;{q.text}&rdquo;
              </blockquote>
            </Scrub>
          ))}
        </div>

        {/* The exchange — one question gets its answer */}
        <div className="mt-32 sm:mt-44 max-w-2xl mx-auto">
          {/* the asking */}
          <Scrub from="right" start="top 90%" end="top 60%" className="flex justify-end">
            <div className="rounded-2xl rounded-br-[4px] bg-[rgba(242,234,216,0.94)] px-5 py-4 max-w-[26rem] shadow-[0_18px_40px_-18px_rgba(0,0,0,0.7)]">
              <p className="text-[#2b2418] text-base sm:text-lg leading-snug">
                What&rsquo;s a good verse for going through a tough time?
              </p>
            </div>
          </Scrub>

          {/* the answer — the app's WordStream reveal, scroll-linked */}
          <div className="mt-12 relative pl-5 sm:pl-6">
            <span
              aria-hidden="true"
              className="absolute left-0 top-1 bottom-1 w-px bg-[rgba(212,163,62,0.55)]"
            />
            <ScrubWords
              className="text-muted-foreground leading-relaxed"
              text="When you are going through a difficult season, the Bible offers words of comfort that remind us we are not alone, that God sees our pain, and that He provides strength when ours runs out."
            />
            <blockquote className="mt-6">
              <ScrubWords
                className="font-scripture text-[1.4rem] sm:text-[1.7rem] leading-[1.4] text-foreground"
                text="“The Lord is near to the brokenhearted and saves the crushed in spirit.”"
                start="top 85%"
                end="top 48%"
              />
              <Scrub from="none" start="top 88%" end="top 62%">
                <cite className="not-italic block mt-3 text-sm font-mono tracking-[0.08em] text-accent">
                  Psalm 34:18, ESV
                </cite>
              </Scrub>
            </blockquote>
            <ScrubWords
              className="text-muted-foreground leading-relaxed mt-6"
              text="When you feel “crushed,” God isn’t distant or judging your struggle. He is closest to you in exactly those moments."
            />
            <Scrub from="none" start="top 92%" end="top 70%">
              <p className="folio mt-8">An example of how Sola answers</p>
            </Scrub>
          </div>

          <Scrub from="up" start="top 94%" end="top 72%">
            <div className="mt-14 flex flex-wrap items-center gap-x-6 gap-y-3">
              <p className="text-sm text-muted-foreground">
                Every answer cites the text, so you can check every one.
              </p>
              <Link
                href="/bible-verse-explained"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground border-b border-accent pb-0.5 hover:text-accent transition-colors"
              >
                Ask about any verse <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </Scrub>
        </div>
      </div>
    </section>
  );
}
